import React, { useContext, useEffect, useCallback, useMemo } from "react";
import SelectLabels from "../../components/SelectLabels";
import { useQuery } from "@tanstack/react-query";
import CashierApi from "../../utility/CashierApi";
import { CustomerContext } from "../CashierHomepage";
import { CustomerIdContext } from "../../context/CustomerIdProvider";

function SelectCustomer() {
  const { fetchData } = CashierApi();

  // ✅ Extract only the values needed from context (prevents full re-render)
  const { setSelectedCustomerName } = useContext(CustomerContext);
  const { setSelectedCustomerId, selectedCustomerId } =
    useContext(CustomerIdContext);

  // ✅ Use TanStack Query to fetch customers with caching & performance optimizations
  const { data } = useQuery({
    queryKey: ["customers"],
    queryFn: () => fetchData("customers"),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    staleTime: 30 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  // ✅ Memoized function to prevent re-renders
  const handleCustomerChange = useCallback(
    (value) => {
      setSelectedCustomerId(value);
    },
    [setSelectedCustomerId] // ✅ Removed unnecessary `setUpdateResumeStatus`
  );

  // ✅ Memoized customer list
  const customersData = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data]
  );

  // ✅ Prevent unnecessary updates when selected customer doesn't change
  useEffect(() => {
    if (!data || !selectedCustomerId) return;

    const selectedCustomer = data.find((obj) => obj.id === selectedCustomerId);
    if (selectedCustomer) {
      setSelectedCustomerName((prevName) =>
        prevName !== selectedCustomer.name ? selectedCustomer.name : prevName
      );
    }
  }, [selectedCustomerId, data, setSelectedCustomerName]);

  return (
    <SelectLabels
      previousValue={selectedCustomerId}
      categoryData={customersData}
      text="Select a Customer"
      valueKey="id"
      labelKey="name"
      onSelectChange={handleCustomerChange}
    />
  );
}

// ✅ Prevents unnecessary re-renders unless props actually change
export default React.memo(SelectCustomer);
